import { PoolEventCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

import { SectionHeader } from '@react/components/common/SectionHeader'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import * as yup from 'yup'

import { Box, Grid, Typography, useTheme } from '@mui/material'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ButtonModal from '@react/components/ButtonModal'
import ViewNumber from '@react/components/elements/ViewNumber'
import { fetchPoolEventCTOs } from '@react/fetchers/PoolFetchers'
import { ERC20Wei, erc20WeiToCommaDecString, erc20WeiToNumber } from '@shared/conversions'
import { RoleEnum, yupErc20WeiRequired } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'

import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { ETH_ADDR_ZERO, EthAddr, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { EventTypeForTemplate } from '@shared/templateApx'
import { useParams } from 'react-router-dom'
import BorrowerAcceptEarlyRedeemRequestForm from './BorrowerAcceptEarlyRedeemRequestForm'
import BorrowerRepayEarlyRedeemForm from './BorrowerRepayEarlyRedeemForm'

export const BorrowerEarlyWithdrawalsList: React.FC<{ poolAddr?: EthAddr }> = (props) => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum, signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()

  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const theme = useTheme()

  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolOverviewCTO
  const [withdrawEarlyEventCTOs, setWithdrawEarlyEventCTOs] = useState<PoolEventCTO[]>([])

  const loanAddr = poolOverviewCTO?.activeLoanState.loanAddr

  useEffect(() => {
    async function fetchEarlyWithdraws(): Promise<void> {
      if (providerReadOnly && signer && loanAddr && loanAddr !== ETH_ADDR_ZERO) {
        logOT.info('fetchEarlyWithdraws  Start loanAddr', loanAddr)

        const allPoolEvents = await fetchPoolEventCTOs(serverApiBaseUrl, restAPIKey, poolAddr)
        logOT.info('fetchEarlyWithdraws  allPoolEvents', allPoolEvents)

        const withdrawEvents = allPoolEvents.filter((e) => {
          if (
            e.eventType === EventTypeForTemplate.WithdrawEarlyRequested ||
            e.eventType === EventTypeForTemplate.WithdrawEarlyAccepted ||
            e.eventType === EventTypeForTemplate.WithdrawEarlyRepay
          ) {
            return e.loanAddr === loanAddr
          }
          return false
        })
        console.log('withdrawEvents', withdrawEvents)

        const eventsWithDupes = withdrawEvents.filter((e) => {
          if (e.eventType === EventTypeForTemplate.WithdrawEarlyRequested) {
            if (
              allPoolEvents.find((e2: PoolEventCTO) => {
                if (e2.eventType === EventTypeForTemplate.WithdrawEarlyAccepted && e2.timestamp > e.timestamp) {
                  return true
                }

                return false
              })
            ) {
              return false
            }
          }
          if (e.eventType === EventTypeForTemplate.WithdrawEarlyAccepted) {
            console.log('Accepted', e)

            if (
              allPoolEvents.find(
                (e2: PoolEventCTO) =>
                  e2.eventType === EventTypeForTemplate.WithdrawEarlyRepay &&
                  e2.withdrawSequenceId === e.withdrawSequenceId &&
                  e2.timestamp > e.timestamp
              )
            ) {
              const sortedEvent = allPoolEvents.sort((a, b) => {
                return a.timestamp - b.timestamp
              })
              let last = null
              let secondLast = null

              // Iterate from the end
              for (let i = sortedEvent.length - 1; i >= 0; i--) {
                if (sortedEvent[i].eventType === EventTypeForTemplate.WithdrawEarlyAccepted) {
                  if (!last) {
                    last = sortedEvent[i] // Set last match
                  } else if (!secondLast) {
                    secondLast = sortedEvent[i] // Set second-to-last match
                    break // Stop early once both are found
                  }
                }
              }
              let requestCount = 0
              const lastTimestamp = last?.timestamp ?? 0
              const secondLastTimestamp = secondLast?.timestamp ?? 0
              for (let i = 0; i < sortedEvent.length; i++) {
                if (sortedEvent[i].eventType === EventTypeForTemplate.WithdrawEarlyRequested) {
                  if (sortedEvent[i].timestamp > secondLastTimestamp && sortedEvent[i].timestamp < lastTimestamp) {
                    requestCount++
                  }
                }
              }

              let repayCount = 0

              for (let i = 0; i < sortedEvent.length; i++) {
                if (sortedEvent[i].eventType === EventTypeForTemplate.WithdrawEarlyRepay) {
                  if (sortedEvent[i].timestamp > lastTimestamp) {
                    repayCount++
                  }
                }
              }
              console.log('requestCount', requestCount, 'repayCount', repayCount)
              if (repayCount >= requestCount) {
                return true
              }
            }
          }
          return true
        })

        console.log('eventsWithDupes', eventsWithDupes)
        const eventsAcceptedMap = new Map<string, PoolEventCTO>()
        const eventsRequestedMap = new Map<string, PoolEventCTO>()

        eventsWithDupes
          .filter((e) => e.eventType === EventTypeForTemplate.WithdrawEarlyAccepted)
          .forEach((event) => {
            console.log('Accepted KEY', event)
            const key = `${event.eventType}-${event.accountAddr}`
            if (eventsAcceptedMap.has(key)) {
              console.log('Accepted key ADD', key)
              const existingEvent = eventsAcceptedMap.get(key)!
              // Sum the fields
              existingEvent.assets = (BigInt(existingEvent.assets) + BigInt(event.assets)).toString() as ERC20Wei
              existingEvent.shares = (BigInt(existingEvent.shares) + BigInt(event.shares)).toString() as ERC20Wei
              existingEvent.principal = (
                BigInt(existingEvent.principal) + BigInt(event.principal)
              ).toString() as ERC20Wei
            } else {
              if (key !== 'WithdrawEarlyAccepted-0xc09E8A0D3158e9b253e23b1f511cF34569BD47f9') {
                console.log('Accepted key SET', key)
                // Add new unique event to the map
                eventsAcceptedMap.set(key, { ...event })
              }
            }
          })
        eventsWithDupes
          .filter((e) => e.eventType === EventTypeForTemplate.WithdrawEarlyRequested)
          .forEach((event) => {
            const key = `${event.eventType}-${event.accountAddr}`
            console.log('Requested key', key)
            if (eventsRequestedMap.has(key)) {
              console.log('Request key ADD', key)
              const existingEvent = eventsRequestedMap.get(key)!
              // Sum the fields
              existingEvent.assets = (BigInt(existingEvent.assets) + BigInt(event.assets)).toString() as ERC20Wei
              existingEvent.shares = (BigInt(existingEvent.shares) + BigInt(event.shares)).toString() as ERC20Wei
              existingEvent.principal = (
                BigInt(existingEvent.principal) + BigInt(event.principal)
              ).toString() as ERC20Wei
            } else {
              // Add new unique event to the map
              console.log('Request key SET', key)
              eventsRequestedMap.set(key, { ...event })
            }
          })

        const events = [...eventsAcceptedMap.values(), ...eventsRequestedMap.values()]

        console.log('events', events)

        //        const requests = await getPoolTermEarlyWithdrawEventCTOs(restAPIKey, graphqlUrl, poolAddr, loanAddr, null)
        setWithdrawEarlyEventCTOs(events)

        logOT.info('fetchEarlyWithdraws Returned requests', events)
      }
    }

    fetchEarlyWithdraws().catch((e) => {
      logOT.info('error', e)
    })
  }, [poolOverviewCTO, loanAddr])

  if (!poolOverviewCTO) {
    return <></>
  }

  const earlyAssets = Math.round(
    erc20WeiToNumber(poolOverviewCTO.totalEarlyRequestedAssets) * 1e6 +
      erc20WeiToNumber(poolOverviewCTO.totalEarlyAcceptedAssets) * 1e6
  )

  const totalEarlyAssetSchema = yup.object({
    totalEarlyAssetField: yupErc20WeiRequired.label('Assets At maturity to be Terminated Early').meta(
      toFieldMetaData({
        propName: 'totalAssetsWithdrawn',
        description:
          'The total amount of assets withdrawn since vault inception.Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  })

  const liquidityTokenSymbol = poolOverviewCTO.liquidityTokenSymbol

  return (
    <Box sx={{ paddingTop: 2 }}>
      <SectionHeader>Early Withdrawal Requests</SectionHeader>
      <Box sx={{ padding: 4, marginBottom: 4 }}>
        <Box sx={{ marginBottom: 4 }}>
          <ViewNumber
            toolTip="The amount of Loan Assets at Maturity that is currently in outstanding Early Withdrawal Requests"
            label="Assets At maturity to be Terminated Early"
            inTable={false}
            liquidityTokenSymbol={liquidityTokenSymbol}
            poolTokenSymbol=""
            dataValue={`${earlyAssets}`}
            fieldSchema={totalEarlyAssetSchema.describe().fields.totalEarlyAssetField}
            value={`${earlyAssets}`}
          />
        </Box>
        {/*  Table header */}
        <Grid container spacing={2} sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
          <Grid item xs={3}>
            <TableHeaderItem>Lender</TableHeaderItem>
          </Grid>
          <Grid item xs={3}>
            <TableHeaderItem>Request Amount</TableHeaderItem>
          </Grid>
          <Grid item xs={3}>
            <TableHeaderItem>Principal({poolOverviewCTO.liquidityTokenSymbol})</TableHeaderItem>
          </Grid>
          <Grid item xs={3}>
            <TableHeaderItem>Action</TableHeaderItem>
          </Grid>
        </Grid>
        {withdrawEarlyEventCTOs
          .filter((e: PoolEventCTO) => e.eventType !== 'WithdrawEarlyRepay')
          .map((req: PoolEventCTO) => {
            let action = 'none'
            if (req.eventType === 'WithdrawEarlyAccepted') {
              action = 'WithdrawEarlyRepay'
            } else if (req.eventType === 'WithdrawEarlyRepay') {
              action = 'Repaid'
            } else {
              action = 'WithdrawEarlyAccept'
            }
            const principal = req.principal
              ? Number(req.principal)
              : ((req.shares ? Number(req.shares) : 0) *
                  erc20WeiToNumber(poolOverviewCTO.activeLoanState.exchangeRateAtDeposit)) /
                1e12

            const shares = req.shares
              ? Number(req.shares)
              : Math.floor((principal * 1e12) / erc20WeiToNumber(poolOverviewCTO.activeLoanState.exchangeRateAtDeposit))

            return (
              <Grid
                container
                spacing={2}
                sx={{ borderBottomWidth: 1, borderBottomColor: theme.palette.divider, marginTop: 2, gap: 2 }}
                key={req.id}
              >
                <Grid item xs={3}>
                  <AddressPill addr={req.accountAddr} />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="bodyBold">
                    {erc20WeiToCommaDecString(shares)}
                    {poolOverviewCTO.symbol}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="bodyBold">{erc20WeiToCommaDecString(principal)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  {action === 'WithdrawEarlyAccept' && (
                    <ButtonModal
                      title={'Accept Early Withdraw Request'}
                      variant="primary"
                      disabled={roleEnum !== RoleEnum.BORROWER}
                    >
                      <BorrowerAcceptEarlyRedeemRequestForm
                        poolAddr={poolAddr}
                        lenderAddr={req.accountAddr}
                        data={{ amount: shares ?? 0, principal: principal, symbol: poolOverviewCTO.symbol }}
                      ></BorrowerAcceptEarlyRedeemRequestForm>
                    </ButtonModal>
                  )}
                  {action === 'WithdrawEarlyRepay' && (
                    <ButtonModal
                      title={'Set Early Withdrawal Amount'}
                      variant="primary"
                      disabled={roleEnum !== RoleEnum.BORROWER}
                    >
                      <BorrowerRepayEarlyRedeemForm
                        poolAddr={poolAddr}
                        lenderAddr={req.accountAddr}
                        data={{
                          amount: req.shares ?? 0,
                          principal: req.principal ?? 0,
                          symbol: poolOverviewCTO.symbol,
                        }}
                      ></BorrowerRepayEarlyRedeemForm>
                    </ButtonModal>
                  )}
                </Grid>
              </Grid>
            )
          })}
        {withdrawEarlyEventCTOs.length === 0 && (
          <Box sx={{ padding: 4 }}>
            <Typography variant="bodyMuted">No Early Withdrawal Requests at this time.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
